<template>
  <HLSpace vertical :size="24">
    <!-- Page Header -->
    <HLCard>
      <div class="p-6">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <HLSpace vertical :size="8">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
              <Icon name="bell" size="lg" class="mr-3 text-primary-600 dark:text-primary-400" />
              Notifications
            </h1>
            <p class="text-gray-600 dark:text-gray-300">
              Stay updated with your project activities and important updates
            </p>
          </HLSpace>
          
          <HLSpace :size="12">
            <HLButton
              @click="markAllAsRead"
              :disabled="!hasUnreadNotifications"
              color="blue"
              variant="primary"
              size="md"
            >
              <template #iconLeft>
                <Icon name="checkCircle" size="sm" />
              </template>
              Mark All as Read
            </HLButton>
            
            <HLButton
              @click="refreshNotifications"
              :disabled="loading"
              color="gray"
              variant="secondary"
              size="md"
            >
              <template #iconLeft>
                <Icon name="refresh" size="sm" :class="{ 'animate-spin': loading }" />
              </template>
              Refresh
            </HLButton>
          </HLSpace>
        </div>
      </div>
    </HLCard>

    <!-- Filters -->
    <HLCard>
      <div class="p-6">
        <HLSpace :size="16" wrap>
          <HLSpace :size="8">
            <HLButton
              @click="filter = 'all'"
              :color="filter === 'all' ? 'blue' : 'gray'"
              :variant="filter === 'all' ? 'primary' : 'secondary'"
              size="sm"
            >
              All ({{ notifications.length }})
            </HLButton>
            <HLButton
              @click="filter = 'unread'"
              :color="filter === 'unread' ? 'blue' : 'gray'"
              :variant="filter === 'unread' ? 'primary' : 'secondary'"
              size="sm"
            >
              Unread ({{ unreadCount }})
            </HLButton>
          </HLSpace>
          
          <HLSpace :size="8" align="center">
            <label class="text-sm text-gray-600 dark:text-gray-400">Type:</label>
            <select
              v-model="typeFilter"
              class="form-select text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg"
            >
              <option value="">All Types</option>
              <option value="bug">Bug</option>
              <option value="test">Test</option>
              <option value="project">Project</option>
              <option value="system">System</option>
            </select>
          </HLSpace>
        </HLSpace>
      </div>
    </HLCard>

    <!-- Notifications List -->
    <HLCard>
      <HLSpin v-if="loading" :spinning="loading" size="large">
        <div class="p-12 text-center">
          <p class="mt-4 text-gray-600 dark:text-gray-400">Loading notifications...</p>
        </div>
      </HLSpin>
      
      <HLEmpty 
        v-else-if="filteredNotifications.length === 0"
        :description="filter === 'unread' ? 'You\'re all caught up!' : 'No notifications match your current filters.'"
        class="py-12"
      >
        <template #image>
          <div class="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="bell" size="xl" class="text-gray-400" />
          </div>
        </template>
        <template #default>
          <p class="text-lg font-medium text-gray-900 dark:text-white mb-2">No notifications found</p>
        </template>
      </HLEmpty>
      
      <div v-else class="divide-y divide-gray-200 dark:divide-gray-700">
        <div
          v-for="notification in filteredNotifications"
          :key="notification.id"
          class="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 cursor-pointer"
          :class="{ 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500': !notification.read }"
          @click="handleNotificationClick(notification)"
        >
          <div class="flex items-start space-x-4">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 rounded-full flex items-center justify-center"
                   :class="getNotificationIconClass(notification.type)">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path v-if="notification.type === 'bug'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  <path v-else-if="notification.type === 'test'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path v-else-if="notification.type === 'project'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <p class="text-sm font-medium text-gray-900 dark:text-white leading-relaxed">
                  {{ notification.message }}
                </p>
                <div class="flex items-center space-x-2">
                  <span v-if="!notification.read" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200">
                    New
                  </span>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ formatTime(notification.created_at) }}
                  </span>
                </div>
              </div>
              
              <div v-if="notification.details" class="mt-2">
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ notification.details }}
                </p>
              </div>
              
              <div v-if="notification.actionUrl" class="mt-3">
                <HLButton
                  @click.stop="navigateToAction(notification.actionUrl)"
                  color="blue"
                  variant="text"
                  size="sm"
                >
                  View Details
                  <template #iconRight>
                    <Icon name="chevronRight" size="sm" />
                  </template>
                </HLButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HLCard>
  </HLSpace>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { formatDistanceToNow } from 'date-fns'
import { HLButton, HLSpace, HLCard, HLSpin, HLEmpty } from '@platform-ui/highrise'
import Icon from '@/components/ui/Icon.vue'

// Router
const router = useRouter()

// Reactive data
const loading = ref(false)
const filter = ref('all')
const typeFilter = ref('')
const notifications = ref([])

// Computed properties
const filteredNotifications = computed(() => {
  let filtered = notifications.value
  
  // Filter by read status
  if (filter.value === 'unread') {
    filtered = filtered.filter(n => !n.read)
  }
  
  // Filter by type
  if (typeFilter.value) {
    filtered = filtered.filter(n => n.type === typeFilter.value)
  }
  
  return filtered
})

const unreadCount = computed(() => {
  return notifications.value.filter(n => !n.read).length
})

const hasUnreadNotifications = computed(() => {
  return unreadCount.value > 0
})

// Methods
const refreshNotifications = async () => {
  loading.value = true
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    await loadNotifications()
  } catch (error) {
    console.error('Error refreshing notifications:', error)
  } finally {
    loading.value = false
  }
}

const loadNotifications = async () => {
  // Mock notifications data - replace with real API call
  notifications.value = [
    {
      id: 1,
      type: 'bug',
      message: 'New bug assigned to you: Login page not working',
      details: 'Critical bug affecting user authentication. Please investigate and fix as soon as possible.',
      read: false,
      created_at: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
      actionUrl: '/bugs/1'
    },
    {
      id: 2,
      type: 'test',
      message: 'Test run "Sprint 5 Regression" completed',
      details: 'All 45 test cases passed successfully. No issues found.',
      read: true,
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
      actionUrl: '/test-runs/123'
    },
    {
      id: 3,
      type: 'project',
      message: 'Project "E-commerce Platform" deadline updated',
      details: 'New deadline: December 15, 2024. Please update your sprint planning accordingly.',
      read: false,
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 4), // 4 hours ago
      actionUrl: '/projects/ecommerce-platform'
    },
    {
      id: 4,
      type: 'system',
      message: 'System maintenance scheduled for tonight',
      details: 'Maintenance window: 2:00 AM - 4:00 AM EST. Expect brief service interruptions.',
      read: true,
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 6), // 6 hours ago
      actionUrl: null
    }
  ]
}

const markAsRead = async (notificationId) => {
  try {
    // Mark notification as read in the backend
    const notification = notifications.value.find(n => n.id === notificationId)
    if (notification && !notification.read) {
      notification.read = true
      // Here you would make an API call to update the notification status
    }
  } catch (error) {
    console.error('Error marking notification as read:', error)
  }
}

const markAllAsRead = async () => {
  try {
    // Mark all notifications as read
    notifications.value.forEach(n => n.read = true)
    // Here you would make an API call to update all notifications
  } catch (error) {
    console.error('Error marking all notifications as read:', error)
  }
}

const handleNotificationClick = (notification) => {
  // Mark as read when clicked
  if (!notification.read) {
    markAsRead(notification.id)
  }
  
  // Navigate to action URL if available
  if (notification.actionUrl) {
    navigateToAction(notification.actionUrl)
  }
}

const navigateToAction = (url) => {
  router.push(url)
}

const getNotificationIconClass = (type) => {
  const baseClasses = 'w-12 h-12 rounded-full flex items-center justify-center'
  
  switch (type) {
    case 'bug':
      return `${baseClasses} bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400`
    case 'test':
      return `${baseClasses} bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400`
    case 'project':
      return `${baseClasses} bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400`
    case 'system':
      return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400`
    default:
      return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400`
  }
}

const formatTime = (timestamp) => {
  return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
}

// Lifecycle
onMounted(async () => {
  await loadNotifications()
})
</script> 